/**
 * SWISS Admin — Shared enums + display labels
 * =============================================
 * The backend is live so the seed* fixture functions that used
 * to live here are gone. What remains are the label sets that
 * the UI uses to render dropdowns, status pills, and group
 * headings. The backend Lambda keeps its own list of phase keys
 * (`PHASE_KEYS` in admin-lambda/index.js) and the two must stay
 * in sync if you add a phase or rename one.
 */

export const SCHOOL_ADMIN_ROLES = [
  { value: 'principal', label: 'Principal' },
  { value: 'wellness_lead', label: 'Wellness Lead' },
  { value: 'counselor', label: 'Counselor' },
  { value: 'admin_staff', label: 'Admin Staff' },
];

// SWISS programme tiers — `value` is the stored tier key, `label` is
// what the admin sees in the dropdown. Which dashboard modules each
// tier unlocks lives in src/services/tiers.js. Keep the order in step
// with TIER_ORDER there.
export const SCHOOL_TIERS = [
  { value: 'on_demand', label: 'SWISS On-Demand' },
  { value: 'bridge', label: 'SWISS Bridge' },
  { value: 'lite', label: 'SWISS Lite' },
  { value: 'essentials', label: 'SWISS Essentials' },
  { value: 'compliance', label: 'SWISS Compliance' },
  { value: 'complete', label: 'SWISS Complete' },
];

// Academic boards / examination councils. `value` is what we store;
// `label` is the human-readable display. Covers India's three major
// boards (CBSE, ICSE/ISC, State), the two main international ones
// (IB, IGCSE/Cambridge), the open-schooling board (NIOS) and an
// `other` catch-all. Add new entries here as a single source of truth.
export const SCHOOL_BOARDS = [
  { value: '',          label: '— Not specified —' },
  { value: 'cbse',      label: 'CBSE' },
  { value: 'icse',      label: 'ICSE (Class I–X)' },
  { value: 'isc',       label: 'ISC (Class XI–XII)' },
  { value: 'state',     label: 'State Board' },
  { value: 'ib',        label: 'IB (International Baccalaureate)' },
  { value: 'igcse',     label: 'IGCSE / Cambridge' },
  { value: 'nios',      label: 'NIOS (Open Schooling)' },
  { value: 'other',     label: 'Other' },
];

// Quick lookup for the display label given a stored key.
export const SCHOOL_BOARD_LABEL = Object.fromEntries(
  SCHOOL_BOARDS.map((b) => [b.value, b.label]),
);

// Mirrors SWISS_POLICIES in the admin Lambda. Keys MUST match —
// backend uses these as the source of truth for cert readiness.
export const SWISS_POLICIES = [
  { key: 'student_wellbeing',   label: 'Student Wellbeing' },
  { key: 'staff_wellbeing',     label: 'Staff Wellbeing' },
  { key: 'grievance_redressal', label: 'Grievance Redressal' },
  { key: 'anti_bullying',       label: 'Anti-Bullying' },
];

export const COUNCIL_MIN_MEMBERS = 5;

export const COUNCIL_ROLES = [
  { value: 'chair', label: 'Council Chair' },
  { value: 'parent_rep', label: 'Parent Representative' },
  { value: 'teacher_rep', label: 'Teacher Representative' },
  { value: 'student_rep', label: 'Student Representative' },
  { value: 'counselor', label: 'School Counselor' },
  { value: 'admin_staff', label: 'School Administrative Staff' },
  { value: 'nurse', label: 'School Nurse' },
  { value: 'external_expert', label: 'External Wellness Expert' },
];

// Single source of truth for which roles a Wellness Council MUST have
// filled to count as fully formed. Used by the Dashboard's council
// stat card and the Council page header. SWISS standard composition:
// one of each of the four constituencies (principal/admin, mental
// health, faculty, parent, student).
export const COUNCIL_REQUIRED_ROLES = [
  'chair',
  'counselor',
  'teacher_rep',
  'parent_rep',
  'student_rep',
];

export const CERTIFICATION_PHASES = [
  { key: 'onboarding', label: 'Onboarding', description: 'Kick-off, MoU, council formation' },
  { key: 'teachers', label: 'Teacher Training', description: 'Gatekeeper and bystander certification' },
  { key: 'policy', label: 'Policy Review', description: 'Wellness policy adoption' },
  { key: 'labs', label: 'Teacher Labs', description: 'Quarterly burnout / wellbeing labs' },
  { key: 'certified', label: 'Certified', description: 'Full SWISS certification granted' },
];

export const PHASE_STATUS = [
  { value: 'not_started', label: 'Not started' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

export const INCIDENT_TYPES = [
  { value: 'student_distress', label: 'Student Distress' },
  { value: 'bullying', label: 'Bullying' },
  { value: 'grief', label: 'Grief / Loss' },
  { value: 'self_harm', label: 'Self-harm risk' },
  { value: 'abuse', label: 'Suspected Abuse' },
  { value: 'other', label: 'Other' },
];

export const INCIDENT_SEVERITY = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export const INCIDENT_STATUS = [
  { value: 'reported', label: 'Reported' },
  { value: 'triage', label: 'In Triage' },
  { value: 'active', label: 'Active Response' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'resolved', label: 'Resolved' },
];

export const INCIDENT_ACTION_TYPES = [
  { value: 'contact_made', label: 'Contact made with student' },
  { value: 'parent_contacted', label: 'Parents contacted' },
  { value: 'counseling', label: 'Counseling session' },
  { value: 'safety_plan', label: 'Safety plan updated' },
  { value: 'external_referral', label: 'External referral' },
  { value: 'status_change', label: 'Status update' },
  { value: 'resolution', label: 'Resolution' },
];
